import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import WebmasterMedicSidebar from "../../components/WebmasterMedicSidebar";
import { useAuth } from "../../context/AuthProvider";
import API_BASE_URL from "../../config/apiConfig";
import Swal from "sweetalert2";
import back from "../../assets/images/icon-back.svg";

const AddPatientMedic: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<"full" | "consult">("full");

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    cnp: "",
    type: "",
    das: "",
    age_range: "",
    category: "",
    parentFirstName: "",
    parentLastName: "",
    parentEmail: "",
    parentPhone: "",
    country: "",
    zone: "",
    city: "",
  });

  const [patientTypes, setPatientTypes] = useState<{ key: string; value: string }[]>([]);
  const [ageRanges, setAgeRanges] = useState<{ key: string; value: string }[]>([]);
  const [categories, setCategories] = useState<{ key: string; value: string }[]>([]);
  const [allCountries, setAllCountries] = useState<{ key: string; value: string }[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<{ key: string; value: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsedMedic") === "true";
  });

  const toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
    didOpen: (t) => {
      t.onmouseenter = Swal.stopTimer;
      t.onmouseleave = Swal.resumeTimer;
    },
  });

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const fetchData = async (endpoint: string) => {
          const response = await fetch(`${API_BASE_URL}/types/${endpoint}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await response.json();
          return data.data;
        };

        const [types, ranges, cats] = await Promise.all([
          fetchData("patient-types"),
          fetchData("age-ranges"),
          fetchData("categories"),
        ]);

        setPatientTypes(types || []);
        setAgeRanges(ranges || []);
        setCategories(cats || []);
      } catch {
        toast.fire({ icon: "error", title: "Eroare la încărcarea listelor dropdown" });
      }
    };

    if (token) fetchDropdowns();
  }, [token]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/types/get-countries`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        const countries = Array.isArray(data) ? data : data?.data || [];
        setAllCountries(countries);
      } catch {
        toast.fire({ icon: "error", title: "Eroare la încărcarea țărilor." });
        setAllCountries([]);
      }
    };

    if (token) fetchCountries();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((p) => ({ ...p, country: value }));

    if (value.length > 1) {
      const filtered = allCountries.filter((c) =>
        c.value.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCountries(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleCountrySelect = (country: string) => {
    setFormData((p) => ({ ...p, country }));
    setShowSuggestions(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let url = "";
      let payload: any = {};

      if (mode === "consult") {
        if (!formData.firstname || !formData.lastname || !formData.parentPhone) {
          toast.fire({ icon: "error", title: "Completează nume, prenume și telefon." });
          return;
        }

        url = `${API_BASE_URL}/patient/add-consult`;
        payload = {
          firstname: formData.firstname,
          lastname: formData.lastname,
          parentPhone: formData.parentPhone,
        };
      } else {
        const cnpRegex = /^[0-9]{13}$/;
        if (!cnpRegex.test(formData.cnp)) {
          toast.fire({ icon: "error", title: "CNP-ul trebuie să conțină exact 13 cifre." });
          return;
        }

        url = `${API_BASE_URL}/patient/add`;

        // ✅ normalize parentEmail
        payload = {
          ...formData,
          parentEmail: (formData.parentEmail || "").trim().toLowerCase(),
        };
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || "A apărut o eroare.");
      }

      toast.fire({ icon: "success", title: "Pacient adăugat cu succes!" });
      setTimeout(() => navigate("/webmaster-medic/patients"), 1500);
    } catch (err: any) {
      toast.fire({ icon: "error", title: err.message || "Eroare la adăugare." });
    }
  };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterMedicSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />
      <div className="page-content">
        <h1 className="page-title">Adaugă Pacient</h1>

        <div className="table-top">
          <div className="page-return">
            <button className="button-return" onClick={() => navigate(-1)}>
              <img src={back} alt="Înapoi" className="back-icon" /> Înapoi
            </button>
          </div>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <p>Tip adăugare:</p>
              <div className="tip-adaugare-pacient">
                <label>
                  <input type="radio" checked={mode === "full"} onChange={() => setMode("full")} />{" "}
                  Pacient normal
                </label>
                <label>
                  <input type="radio" checked={mode === "consult"} onChange={() => setMode("consult")} />{" "}
                  Consult medical (rapid)
                </label>
              </div>
            </div>

            <div className="form-row">
              <label>Nume:</label>
              <input type="text" name="lastname" value={formData.lastname} onChange={handleChange} placeholder="Numele" required />
            </div>

            <div className="form-row">
              <label>Prenume:</label>
              <input type="text" name="firstname" value={formData.firstname} onChange={handleChange} placeholder="Prenumele" required />
            </div>

            {mode === "consult" && (
              <div className="form-row">
                <label>Telefon:</label>
                <input type="text" name="parentPhone" value={formData.parentPhone} onChange={handleChange} placeholder="Telefon" required />
              </div>
            )}

            {mode === "full" && (
              <>
                <div className="form-row">
                  <label>CNP:</label>
                  <input type="text" name="cnp" value={formData.cnp} onChange={handleChange} placeholder="CNP" maxLength={13} required />
                </div>

                <div className="form-row">
                  <label>Tip:</label>
                  <select name="type" value={formData.type} onChange={handleChange} required>
                    <option value="">Selectează tipul</option>
                    {patientTypes.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.value}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.type === "intern" && (
                  <div className="form-row">
                    <label>DAS:</label>
                    <input type="text" name="das" value={formData.das} onChange={handleChange} placeholder="DAS" />
                  </div>
                )}

                <div className="form-row flex-row">
                  <div className="flex-input">
                    <label>Vârstă:</label>
                    <select name="age_range" value={formData.age_range} onChange={handleChange} required>
                      <option value="">Selectează vârsta</option>
                      {ageRanges.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.value}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-input">
                    <label>Categorie:</label>
                    <select name="category" value={formData.category} onChange={handleChange} required>
                      <option value="">Selectează categoria</option>
                      {categories.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <h3 className="form-subtitle">Reprezentant Legal</h3>

                <div className="form-row flex-row">
                  <div className="flex-input">
                    <label>Nume:</label>
                    <input type="text" name="parentLastName" value={formData.parentLastName} onChange={handleChange} placeholder="Nume reprezentant legal" required />
                  </div>
                  <div className="flex-input">
                    <label>Prenume:</label>
                    <input type="text" name="parentFirstName" value={formData.parentFirstName} onChange={handleChange} placeholder="Prenume reprezentant legal" required />
                  </div>
                </div>

                <div className="form-row flex-row">
                  <div className="flex-input">
                    <label>Email:</label>
                    <input type="email" name="parentEmail" value={formData.parentEmail} onChange={handleChange} placeholder="Adresa de e-mail" required />
                  </div>
                  <div className="flex-input">
                    <label>Telefon:</label>
                    <input type="text" name="parentPhone" value={formData.parentPhone} onChange={handleChange} placeholder="Telefon" required />
                  </div>
                </div>

                <div className="form-row autocomplete-wrapper">
                  <label>Țară:</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleCountryChange}
                    placeholder="Țara"
                    autoComplete="off"
                    required
                  />
                  {showSuggestions && filteredCountries.length > 0 && (
                    <ul className="autocomplete-list">
                      {filteredCountries.map((c) => (
                        <li key={c.key} onClick={() => handleCountrySelect(c.value)}>
                          {c.value}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="form-row flex-row">
                  <div className="flex-input">
                    <label>Județ/Regiune:</label>
                    <input type="text" name="zone" value={formData.zone} onChange={handleChange} placeholder="Județ/Regiune" required />
                  </div>
                  <div className="flex-input">
                    <label>Localitate:</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="Localitate" required />
                  </div>
                </div>
              </>
            )}

            <div className="form-row form-actions">
              <button className="cancel-btn" onClick={() => navigate(-1)} type="button">
                Anulează
              </button>
              <button className="primary-btn" type="submit">
                Adaugă
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPatientMedic;